// OpenDotaMatchCache.js
// ---------------------------------------------------------------------------
// Fetches + caches full OpenDota match JSON, for Scout Simulator's per-pick
// "why" insight (see openDotaPickInsight.js). Pro matches are static once
// played, so a long-lived localStorage cache is safe — no invalidation
// needed beyond a generous expiry as a hygiene backstop.
//
// Public pro matches are usually already parsed by OpenDota by the time they
// land in pro_matches (no polling/parse-request flow here — if a match comes
// back unparsed, callers just get reduced data, same graceful-degrade
// pattern as everywhere else in this feature).
// ---------------------------------------------------------------------------

const OPENDOTA_API = 'https://api.opendota.com/api';
const CACHE_PREFIX = 'opendota_match_';
const CACHE_EXPIRY_DAYS = 30;
const FETCH_TIMEOUT_MS = 10000;

function readCache(matchId) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + matchId);
    if (!raw) return undefined;
    const { data, timestamp } = JSON.parse(raw);
    const ageMs = Date.now() - timestamp;
    if (ageMs > CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(CACHE_PREFIX + matchId);
      return undefined;
    }
    return data; // may itself be null (a cached "fetch failed / not found")
  } catch {
    return undefined; // corrupt entry — treat as a miss
  }
}

function writeCache(matchId, data) {
  try {
    localStorage.setItem(CACHE_PREFIX + matchId, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // Quota exceeded or storage disabled — cache is a pure optimization,
    // fine to silently skip.
  }
}

async function fetchWithTimeout(url, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// In-flight request de-dupe: two components asking for the same match at
// once (e.g. two candidate rows sharing a sample match) share one fetch.
const inFlight = new Map();

/**
 * @param {string|number} matchId
 * @returns {Promise<object|null>} raw OpenDota match JSON, or null if the
 *   match doesn't exist / isn't parsed / the request failed. Never throws.
 */
export async function getMatch(matchId) {
  const id = String(matchId).replace(/[^0-9]/g, '');
  if (!id) return null;

  const cached = readCache(id);
  if (cached !== undefined) return cached;

  if (inFlight.has(id)) return inFlight.get(id);

  const promise = (async () => {
    try {
      const res = await fetchWithTimeout(`${OPENDOTA_API}/matches/${id}`, FETCH_TIMEOUT_MS);
      if (res.status === 429) {
        // Rate limited — don't cache a negative result, just skip for now
        // and let the next call retry fresh.
        return null;
      }
      if (!res.ok) {
        writeCache(id, null); // 404s etc. are stable — worth caching
        return null;
      }
      const data = await res.json();
      writeCache(id, data);
      return data;
    } catch {
      return null; // network error/timeout — not cached, safe to retry later
    } finally {
      inFlight.delete(id);
    }
  })();

  inFlight.set(id, promise);
  return promise;
}
